import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { UserApiService } from '@dev-fast/backend-services';
import type { Observable } from 'rxjs';
import { catchError, map, of, switchMap, take } from 'rxjs';

import { EnvironmentService } from '@app/core/environment-service';
import { UserService } from '@app/core/user-service';

@Injectable({
  providedIn: 'root',
})
export class WelcomeWorkActivateGuard implements CanActivate {
  readonly #userService = inject(UserService);
  readonly #apiUserService = inject(UserApiService);
  readonly #router = inject(Router);
  readonly #environmentService = inject(EnvironmentService);

  readonly isActiveWelcomeLayout = this.#environmentService.isStore;

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Если WELCOME_LAYOUT выключен, запрещаем доступ к welcome-странице
    if (!this.isActiveWelcomeLayout) {
      return this.#router.createUrlTree(['/']);
    }

    // Если WELCOME_LAYOUT включен, проверяем авторизацию
    return this.#userService.user$.pipe(
      take(1),
      switchMap((user) => {
        if (user !== undefined) {
          return of(user);
        }
        return this.#apiUserService.getMeProfile({ detailed: false });
      }),
      map((user) => {
        // Если пользователь авторизован, перенаправляем на основное приложение
        if (user) {
          return this.#router.createUrlTree(['/'], {
            queryParamsHandling: 'merge',
          });
        }
        // Если не авторизован, разрешаем доступ к welcome-странице
        return true;
      }),
      catchError(() => of(true)),
    );
  }
}
